import { isAxiosError } from "axios";
import { t } from "i18next";

export type ApiErrorDetails = {
  /** HTTP status; undefined on network/CORS failures (no response arrived). */
  status?: number;
  /** Machine-readable code from the API error body, when present. */
  code?: string;
  /** Human-readable message from the API error body, when present. */
  message?: string;
};

/**
 * Extract { status, code, message } from an unknown query error. Safe on
 * non-axios errors and on axios errors without a response (network failure):
 * every field is optional and simply absent when unavailable.
 *
 * The codes themselves live in @repo/shared (`AUTHORIZATION_ERROR_CODES`) so the
 * API and the client cannot drift on the vocabulary they use to talk about the
 * same refusal.
 */
export function getApiErrorDetails(error: unknown): ApiErrorDetails {
  if (!isAxiosError(error)) return {};
  const data = error.response?.data as { code?: unknown; message?: unknown } | undefined;
  const rawMessage = Array.isArray(data?.message)
    ? data.message.find((item): item is string => typeof item === "string")
    : data?.message;
  return {
    status: error.response?.status,
    code: typeof data?.code === "string" ? data.code : undefined,
    message: typeof rawMessage === "string" ? rawMessage : undefined,
  };
}

export const GENERIC_ERROR_MESSAGE = "Algo salió mal. Probá de nuevo en unos minutos.";
export const NETWORK_ERROR_MESSAGE = "No pudimos conectarnos. Revisá tu conexión y probá de nuevo.";

/** Clave de i18n que manda la API ("adminCourseView.toast.courseNotFound"). */
const I18N_KEY = /^[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)+$/;

/**
 * El texto que se le muestra a la persona cuando un pedido a la API falla.
 *
 * - Si la API mandó una clave de i18n, la traduce (y si la clave no existe, usa el fallback en vez
 *   de mostrar "adminCourseView.toast.algo").
 * - Si mandó un mensaje, lo usa tal cual: la API garantiza que es castellano apto para el usuario
 *   (filtro global `UserFacingExceptionFilter`).
 * - Nunca muestra el `error.message` de axios ("Network Error", "Request failed with status code
 *   500"): sin respuesta es un problema de conexión, y sin mensaje va el fallback.
 */
export function getApiErrorMessage(error: unknown, fallback: string = GENERIC_ERROR_MESSAGE) {
  if (isAxiosError(error) && !error.response) {
    // Un pedido cancelado a propósito (logout, navegación) no es un problema de conexión.
    return error.code === "ERR_CANCELED" ? fallback : NETWORK_ERROR_MESSAGE;
  }

  const { message } = getApiErrorDetails(error);
  if (!message?.trim()) return fallback;

  if (I18N_KEY.test(message)) {
    const translated = t(message);
    return translated && translated !== message ? translated : fallback;
  }

  return message;
}
